import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ErrorState } from "@/components/shared/error-state";
import { RichTextEditor } from "@/components/shared/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useKickoffEmailTemplate, useSaveKickoffEmailTemplate } from "@/hooks/use-app-settings";
import { parseEmailList } from "@/lib/email-list";
import { GUIDANCE_TEMPLATE_VARIABLES } from "@/lib/guidance-template";

const emailSchema = z.string().email();

const schema = z.object({
  subject: z.string().trim().min(1, "Subject is required"),
  body: z.string().trim().min(1, "Template body is required"),
  cc: z
    .string()
    .refine((raw) => parseEmailList(raw).every((e) => emailSchema.safeParse(e).success), {
      message: "One or more CC addresses aren't valid email addresses",
    }),
});

type FormValues = z.infer<typeof schema>;

export function KickoffEmailTemplateSection() {
  const { data, isLoading, isError, refetch } = useKickoffEmailTemplate();
  const save = useSaveKickoffEmailTemplate();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { subject: "", body: "", cc: "" },
  });

  useEffect(() => {
    if (data) {
      form.reset({ subject: data.subject, body: data.body, cc: data.cc.join("\n") });
    }
  }, [data, form]);

  if (isError) {
    return <ErrorState message="Failed to load kickoff email template" onRetry={() => refetch()} />;
  }

  async function onSubmit(values: FormValues) {
    await save.mutateAsync({
      subject: values.subject,
      body: values.body,
      cc: parseEmailList(values.cc),
    });
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground mb-3">
          Used to fill in the kickoff email a compliance manager can send once per audit, any time
          after guidance has been requested. It goes to the Solution Architect, the CSM and the
          Compliance Auditor, and copies the project's tech partner plus the addresses below.
        </p>
        <div className="mb-6 rounded-md border border-border bg-muted/30 p-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Use <code className="font-mono">{"{{variable_name}}"}</code> in the subject or body to
            substitute:
          </p>
          <ul className="space-y-1">
            {GUIDANCE_TEMPLATE_VARIABLES.map((v) => (
              <li key={v.key} className="text-xs text-muted-foreground">
                <code className="font-mono text-foreground">{`{{${v.key}}}`}</code> — {v.label}:{" "}
                {v.description}
              </li>
            ))}
          </ul>
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subject</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="body"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Template Body</FormLabel>
                  <FormControl>
                    <RichTextEditor
                      id={field.name}
                      ariaLabel="Template body"
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormDescription>
                    A placeholder with no value (for example, guidance hours on an older audit) is
                    left blank in the email.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cc"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CC List</FormLabel>
                  <FormControl>
                    <Textarea
                      className="resize-none font-mono text-sm"
                      rows={3}
                      placeholder="one@example.com, two@example.com"
                      disabled={isLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Separate addresses with commas or new lines. The project's tech partner is
                    always CC'd in addition to these.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end">
              <Button type="submit" disabled={save.isPending || isLoading}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
